import { track } from '@amplitude/analytics-browser'
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import { format } from 'date-fns'
import isEmpty from 'lodash.isempty'
import { useEffect, useState } from 'react'

import { PostPreviewGrid, type PostPreviewGridProps } from 'Components'
import useKeyPress from 'hooks/useKeyPress'
import { ApprovalsIcon, GridPreviewIcon } from 'Icons'
import { ToggleGroup, ToggleGroupItem } from 'ui/toggle-group'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'ui/tooltip'
import type { PreviewVariant } from 'utils/postPreviewVariant'

import Dropdown from './Common/Dropdown'
import PoweredBySked from './Common/PoweredBySked'
import SkedPreviewFrame from './SkedPreviewFrame'

// Up/down post navigator. `active` drives the tinted background + purple icon
// (vs the inert grey state at the list edges).
const NavButton = ({
  active,
  direction,
  onClick,
}: {
  active: boolean
  direction: 'up' | 'down'
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={direction === 'up' ? 'Previous post' : 'Next post'}
    className={cn(
      'grid size-8 place-items-center rounded-[3px]',
      active
        ? 'cursor-pointer bg-[#f6f5ff] text-blue-500'
        : 'cursor-default bg-[#eeeeee] text-neutral-800'
    )}
  >
    <i
      aria-hidden="true"
      className={cn(
        'fa-solid text-[18px]',
        direction === 'up' ? 'fa-chevron-up' : 'fa-chevron-down'
      )}
    />
  </button>
)

export type PostOrGridView = 'post' | 'grid'

const LEGACY_POST_REVIEW_CONTAINER_CLASS =
  'relative flex h-full flex-col max-[1280px]:max-w-none max-w-[360px]'

const UPLIFT_POST_REVIEW_CONTAINER_CLASS =
  'relative mx-auto flex h-full w-full flex-col max-w-[400px]'

export const getPostReviewContainerClassName = (
  previewVariant: PreviewVariant
) =>
  previewVariant === 'uplift'
    ? UPLIFT_POST_REVIEW_CONTAINER_CLASS
    : LEGACY_POST_REVIEW_CONTAINER_CLASS

export interface PostPreviewSectionProps {
  totalPostLength: number
  currentPostIndex: number
  navigatePost: (currentPostIndex: number) => void
  skedPreviewPost: any //refer to https://github.com/DialogueConsulting/sked-external-review/blob/master/src/components/post/Post.js#L14
  postOrGridView: PostOrGridView
  setpostOrGridView: React.Dispatch<React.SetStateAction<PostOrGridView>>
  isGridPreviewAvailable: boolean
  gridData: PostPreviewGridProps
  previewVariant: PreviewVariant
}

const PostPreviewSection: React.FC<PostPreviewSectionProps> = ({
  totalPostLength,
  currentPostIndex,
  navigatePost,
  skedPreviewPost,
  postOrGridView,
  setpostOrGridView,
  isGridPreviewAvailable,
  gridData,
  previewVariant,
}) => {
  const [skedPreviewPostData, setSkedPreviewPostData] = useState<any>({})
  const [multiplePlatformAccounts, setMultiplePlatformAccounts] = useState([])
  const [selectedAccountId, setSelectedAccountId] = useState('')

  const handleNavigateButton = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      currentPostIndex = currentPostIndex - 1
    } else if (direction === 'next') {
      currentPostIndex = currentPostIndex + 1
    }

    navigatePost(currentPostIndex)
  }

  const handlePostOrGridView = (view: PostOrGridView) => () => {
    window.scrollTo(0, 0)
    setpostOrGridView(view)

    if (view === 'post') {
      track('ext-planner-click')
    }
  }

  useEffect(() => {
    if (!isEmpty(skedPreviewPost)) {
      const hasMultiPlatform =
        skedPreviewPost.providedPost?.accounts?.length > 1

      if (hasMultiPlatform) {
        const nextMultiplePlatformAccounts =
          skedPreviewPost.providedPost.accounts.map((account: any) => ({
            id: account.id,
            label: account.name,
            value: account.id,
            avatar: account.avatar,
          }))

        setMultiplePlatformAccounts(nextMultiplePlatformAccounts)
        setSelectedAccountId(skedPreviewPost.providedPost.accounts[0].id)

        const skedPreviewPostsWithPlatform =
          skedPreviewPost.providedPost.accounts.map((account: any) => ({
            ...skedPreviewPost,
            platform: account.platform,
            _id: account.id,
            username: account.name,
            profilePicture: account.avatar,
          }))

        setSkedPreviewPostData(skedPreviewPostsWithPlatform)
      } else {
        setMultiplePlatformAccounts([])
        setSkedPreviewPostData(skedPreviewPost)
      }
    }
  }, [skedPreviewPost])

  useEffect(() => {
    setpostOrGridView('post')
  }, [currentPostIndex])

  useKeyPress('ArrowDown', () => {
    if (currentPostIndex < totalPostLength) {
      handleNavigateButton('next')
    }
  })

  useKeyPress('ArrowUp', () => {
    if (currentPostIndex > 1) {
      handleNavigateButton('prev')
    }
  })

  if (!skedPreviewPost) {
    return (
      <div className="absolute left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2 text-center text-sm text-[#888]">
        Unable to fetch post data.
      </div>
    )
  }

  if (isEmpty(skedPreviewPostData)) return null

  return (
    <div
      data-testid="post-review-container"
      className={getPostReviewContainerClassName(previewVariant)}
    >
      {postOrGridView === 'post' && (
        <>
          <div className="mb-[25px] flex items-center justify-between">
            <h2 className="font-gotham text-xl font-extrabold text-neutral-800">
              Post Preview
            </h2>
            <div className="flex items-center text-[18px] font-bold">
              <NavButton
                active={currentPostIndex > 1}
                direction="up"
                onClick={() => {
                  if (currentPostIndex > 1) {
                    handleNavigateButton('prev')
                  }
                }}
              />
              {currentPostIndex !== 0 && (
                <span className="mx-[6px] min-w-[88px] whitespace-nowrap text-center text-sm">
                  {currentPostIndex} of {totalPostLength} Posts
                </span>
              )}
              <NavButton
                active={currentPostIndex < totalPostLength}
                direction="down"
                onClick={() => {
                  if (currentPostIndex < totalPostLength) {
                    handleNavigateButton('next')
                  }
                }}
              />
            </div>
          </div>

          {['queued', 'scheduled'].includes(skedPreviewPostData.stage) &&
            skedPreviewPostData.scheduledFor && (
              <div className="mb-[10px] text-sm font-medium">
                {format(
                  new Date(skedPreviewPostData.scheduledFor),
                  'EEEE, d LLLL p'
                )}
              </div>
            )}

          {multiplePlatformAccounts.length > 0 && (
            <div className="mb-4">
              <Dropdown
                name="accounts"
                placeholder="Select from options"
                onChange={(selected) => setSelectedAccountId(selected.value)}
                options={multiplePlatformAccounts}
              />
            </div>
          )}

          {multiplePlatformAccounts.length > 0 ? (
            skedPreviewPostData.map(
              (post: any) =>
                post._id === selectedAccountId && (
                  <SkedPreviewFrame
                    key={post._id}
                    post={post}
                    previewVariant={previewVariant}
                    legacyClassName="mx-auto mb-[18px] w-[360px] rounded-[10px] bg-white p-[3px] shadow-[3px_0_10px_rgba(0,0,0,0.15)]!"
                    upliftWrapperClassName="mb-[18px]"
                  />
                )
            )
          ) : (
            <SkedPreviewFrame
              post={skedPreviewPostData}
              previewVariant={previewVariant}
              legacyClassName="mx-auto mb-[18px] w-[360px] rounded-[10px] bg-white p-[3px] shadow-[3px_0_10px_rgba(0,0,0,0.15)]!"
              upliftWrapperClassName="mb-[18px]"
            />
          )}
        </>
      )}

      {postOrGridView === 'grid' && (
        <div className="relative min-h-[510px] overflow-hidden [&_.phone-bg]:left-1/2 [&_.phone-bg]:-translate-x-1/2 md:[&_.container]:mt-[62px] md:[&_.container]:min-h-[450px]">
          <PostPreviewGrid {...gridData} />
        </div>
      )}

      {isGridPreviewAvailable && (
        <ToggleGroup
          type="single"
          appearance="nav"
          value={postOrGridView}
          aria-label="Post or grid view"
          className="mx-auto mt-[30px]"
          onValueChange={(value) => {
            if (value) handlePostOrGridView(value as PostOrGridView)()
          }}
        >
          <ToggleGroupItem value="post">
            <ApprovalsIcon aria-hidden="true" />
            Post Preview
          </ToggleGroupItem>
          <ToggleGroupItem value="grid">
            <GridPreviewIcon aria-hidden="true" />
            Grid View
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-testid="switch-to-grid-view-tooltip"
                    className="inline-flex cursor-help"
                  >
                    <i className="fa-solid fa-circle-question text-base" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  See how this post will look in the Instagram grid
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </ToggleGroupItem>
        </ToggleGroup>
      )}

      <PoweredBySked className="flex flex-1 items-end justify-center py-[30px]" />
    </div>
  )
}

export default PostPreviewSection
